import { formatCpmCount } from '../../constants/centralProductMaster.constants';

interface CpmPaginationProps {
    page: number;
    limit: number;
    total: number;
    onPageChange: (page: number) => void;
    /** Plural noun appended to the range label, e.g. "brands" → "Showing 1–25 of 340 brands". */
    noun?: string;
    /**
     * Suppress the built-in "Showing…" label so a consumer can render it
     * elsewhere in a custom footer bar (e.g. Master Editor's Rows-dropdown row).
     */
    hideLabel?: boolean;
}

export function CpmPaginationLabel({
    page,
    limit,
    total,
    noun,
}: Pick<CpmPaginationProps, 'page' | 'limit' | 'total' | 'noun'>) {
    if (total === 0) return null;
    const from = (page - 1) * limit + 1;
    const to = Math.min(page * limit, total);
    return (
        <span
            className="tabular text-[var(--cpm-text-secondary)]"
            style={{ fontSize: 12.5, fontWeight: 700 }}
        >
            Showing {formatCpmCount(from)}–{formatCpmCount(to)} of {formatCpmCount(total)}
            {noun ? ` ${noun}` : ''}
        </span>
    );
}

function PagerArrow({
    direction,
    disabled,
    onClick,
}: {
    direction: 'prev' | 'next';
    disabled: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className="inline-flex items-center justify-center rounded-[var(--cpm-radius-8)] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)]"
            style={{ width: 32, height: 32, opacity: disabled ? 0.5 : 1 }}
        >
            <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                {direction === 'prev' ? <path d="m15 18-6-6 6-6" /> : <path d="m9 18 6-6-6-6" />}
            </svg>
        </button>
    );
}

export default function CpmPagination({
    page,
    limit,
    total,
    onPageChange,
    noun,
    hideLabel = false,
}: CpmPaginationProps) {
    const totalPages = Math.max(1, Math.ceil(total / limit));
    if (total === 0) return null;

    return (
        <div
            className="flex items-center justify-between bg-[var(--cpm-surface-subtle)] border-t border-[var(--cpm-border-default)] rounded-b-[var(--cpm-radius-2xl)]"
            style={{ gap: 12, padding: '12px 18px' }}
        >
            {!hideLabel ? (
                <CpmPaginationLabel page={page} limit={limit} total={total} noun={noun} />
            ) : (
                <span />
            )}
            <div className="flex items-center" style={{ gap: 8 }}>
                <PagerArrow
                    direction="prev"
                    disabled={page <= 1}
                    onClick={() => onPageChange(page - 1)}
                />
                <span
                    className="text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 12.5, fontWeight: 700 }}
                >
                    Page
                </span>
                <span
                    className="cpm-fw-800 flex items-center justify-center rounded-[var(--cpm-radius-8)] bg-white border-[1.5px] border-[var(--cpm-border-strong)] tabular text-[var(--cpm-text-primary)]"
                    style={{
                        minWidth: 38,
                        height: 32,
                        paddingLeft: 10,
                        paddingRight: 10,
                        fontSize: 13,
                        fontWeight: 800,
                    }}
                >
                    {page}
                </span>
                <span
                    className="tabular text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 12.5, fontWeight: 700 }}
                >
                    of {formatCpmCount(totalPages)}
                </span>
                <PagerArrow
                    direction="next"
                    disabled={page >= totalPages}
                    onClick={() => onPageChange(page + 1)}
                />
            </div>
        </div>
    );
}
